import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { CreativeKind } from '../models/analysis.types'
import type { CreativeLabelDefinition } from '../models/labelSchema.types'
import { isSameLabelValue } from '../service/labelEditValue'

export interface PendingLabelEdit {
  kind: CreativeKind
  creativeId: string
  labelKey: string
  value: unknown
  originalValue: unknown
  creativeName: string
  labelName: string
  displayOld: string
  displayNew: string
}

function keyFor(kind: CreativeKind, creativeId: string, labelKey: string): string {
  return `${kind}:${creativeId}:${labelKey}`
}

export const useCreativeLabelEditStore = defineStore('creativeAiLabelEdit', () => {
  const pending = ref(new Map<string, PendingLabelEdit>())

  function edits(kind: CreativeKind): PendingLabelEdit[] {
    return [...pending.value.values()].filter((edit) => edit.kind === kind)
  }

  function count(kind: CreativeKind): number {
    return edits(kind).length
  }

  function edit(
    kind: CreativeKind,
    creativeId: string,
    labelKey: string,
  ): PendingLabelEdit | undefined {
    return pending.value.get(keyFor(kind, creativeId, labelKey))
  }

  function remove(kind: CreativeKind, creativeId: string, labelKey: string): void {
    const key = keyFor(kind, creativeId, labelKey)
    if (!pending.value.has(key)) return
    const next = new Map(pending.value)
    next.delete(key)
    pending.value = next
  }

  function upsert(
    definitionType: Pick<CreativeLabelDefinition, 'type'>,
    entry: PendingLabelEdit,
  ): void {
    if (isSameLabelValue(definitionType, entry.value, entry.originalValue)) {
      remove(entry.kind, entry.creativeId, entry.labelKey)
      return
    }
    const next = new Map(pending.value)
    next.set(keyFor(entry.kind, entry.creativeId, entry.labelKey), entry)
    pending.value = next
  }

  function clearAll(): void {
    pending.value = new Map()
  }

  function clear(kind: CreativeKind): void {
    const next = new Map(pending.value)
    for (const [key, value] of next) if (value.kind === kind) next.delete(key)
    pending.value = next
  }

  return { pending, edits, count, edit, remove, upsert, clear, clearAll }
})

export type CreativeLabelEditStore = ReturnType<typeof useCreativeLabelEditStore>
